import { useState, useRef, useEffect } from 'react';
import { Download, AlertTriangle } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useMarksCompleteness } from '../hooks/useMarksCompleteness';
import { PendingMarksModal } from './PendingMarksModal';
import { ClassReportTemplate } from './ClassReportTemplate';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, getSetting } from '../db/shiksha-db';
import { auth } from '../services/firebase';

const WORKER_API_URL = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';

export function ReportCardDownloadButton({ classId, students, academicYear, section }) {
  const { isComplete, completionPercent, pendingSubjects } = useMarksCompleteness(classId, students);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const reportTemplateRef = useRef(null);
  const [schoolName, setSchoolName] = useState('RSBRTL Shiksha-Edge');
  const [fetchedMarks, setFetchedMarks] = useState([]);
  const [fetchedCoScholastic, setFetchedCoScholastic] = useState([]);
  const [fetchedAttendance, setFetchedAttendance] = useState([]);
  const [studentRanks, setStudentRanks] = useState({});
  // Load school name from settings
  useEffect(() => {
    getSetting('schoolName').then(name => {
      if (name) setSchoolName(name);
    });
  }, []);

  const schemas = useLiveQuery(() => db.schemas.toArray()) || [];

  const handleDownloadClick = async () => {
    if (!isComplete) {
      setIsModalOpen(true);
      return;
    }

    if (students.length === 0) return;
    if (!navigator.onLine) {
      alert("Active internet connection is required to generate report cards from the Master Sheet.");
      return;
    }

    setIsGenerating(true);
    
    try {
      let idToken = null;
      if (auth.currentUser) {
        idToken = await auth.currentUser.getIdToken(false);
      }
      
      const res = await fetch(`${WORKER_API_URL}/api/marks/report-card?classId=${encodeURIComponent(classId)}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      
      if (!res.ok) throw new Error("Failed to fetch authoritative marks from Master Sheet");
      
      const data = await res.json();
      
      // Parse data
      const parsedMarks = [];
      if (data.marks && data.marks.length > 1) {
        data.marks.slice(1).forEach(row => {
          parsedMarks.push({
            studentId: row[0] ? String(row[0]) : '',
            subject: row[1] || '',
            term: row[2] || '',
            componentId: row[3] || '',
            score: Number(row[4]) || 0,
            maxScore: Number(row[5]) || 0,
          });
        });
      }
      
      const parsedCo = [];
      if (data.coScholastic && data.coScholastic.length > 1) {
        data.coScholastic.slice(1).forEach(row => {
          parsedCo.push({
            studentId: row[0] ? String(row[0]) : '',
            area: row[1] || '',
            grade: row[2] || '',
            term: row[3] || '',
            academicYear: row[4] || '',
          });
        });
      }

      // Aggregate attendance
      // Attendance columns in sheet: StudentID, Date, Status, Timestamp, User
      const aggregatedAttendance = [];
      if (data.attendance && data.attendance.length > 1) {
        const attendanceRecords = data.attendance.slice(1);
        
        students.forEach(student => {
          const studentRecords = attendanceRecords.filter(row => String(row[0]) === String(student.id));
          
          let term1Working = 0;
          let term1Attended = 0;
          let term2Working = 0;
          let term2Attended = 0;
          
          studentRecords.forEach(row => {
            const dateStr = row[1]; // YYYY-MM-DD
            const status = row[2]; // 'Present', 'Absent', 'DELETED'
            if (status === 'DELETED') return;
            
            const month = parseInt(dateStr.split('-')[1], 10);
            
            // Assume Term 1 is April (4) to September (9)
            // Assume Term 2 is October (10) to March (3)
            const isTerm1 = month >= 4 && month <= 9;
            
            if (isTerm1) {
              term1Working++;
              if (status === 'Present') term1Attended++;
            } else {
              term2Working++;
              if (status === 'Present') term2Attended++;
            }
          });
          
          aggregatedAttendance.push({ studentId: student.id, term: 'Term 1', workingDays: term1Working, attendedDays: term1Attended });
          aggregatedAttendance.push({ studentId: student.id, term: 'Term 2', workingDays: term2Working, attendedDays: term2Attended });
        });
      }
      
      setFetchedMarks(parsedMarks);
      setFetchedCoScholastic(parsedCo);
      setFetchedAttendance(aggregatedAttendance);

      // Compute ranks
      const totals = students.map(student => {
        let grandTotal = 0;
        const subjects = ['English', 'Hindi', 'Bengali', 'Mathematics', 'Science', 'Social Science'];
        subjects.forEach(sub => {
          const m = parsedMarks.filter(mk => mk.studentId === student.id && mk.subject === sub);
          const getM = (cid) => m.find(x => x.componentId === cid)?.score || 0;
          if (['Class VI', 'Class VII', 'Class VIII'].includes(classId)) {
            const t1 = (getM('pt1')/2) + (getM('pt2')/2) + (getM('half_yearly')/2) + getM('multiple_assessment1') + getM('portfolio1') + getM('enrichment1');
            const t2 = (getM('pt3')/2) + (getM('pt4')/2) + (getM('annual')/2) + getM('multiple_assessment2') + getM('portfolio2') + getM('enrichment2');
            grandTotal += (t1 * 0.4) + (t2 * 0.6);
          } else {
            const bestTwo = [getM('pre_mid'), getM('mid'), getM('post_mid')].sort((a,b)=>b-a);
            const avgBest2 = ((bestTwo[0] + bestTwo[1])/2) * 0.1;
            grandTotal += avgBest2 + getM('multiple_assessment') + getM('portfolio') + getM('enrichment') + getM('annual');
          }
        });
        return { id: student.id, total: grandTotal };
      });
      totals.sort((a, b) => b.total - a.total);
      
      const ranks = {};
      let currentRank = 1;
      totals.forEach((t, idx) => {
        if (idx > 0 && t.total < totals[idx - 1].total) {
          currentRank = idx + 1;
        }
        ranks[t.id] = currentRank;
      });
      setStudentRanks(ranks);

      // Give react time to render the hidden DOM with fetched data
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = reportTemplateRef.current;
      
      const opt = {
        margin:       10,
        filename:     `Class_${classId}_${section}_Report_Cards_${academicYear}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2 },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      await html2pdf().from(element).set(opt).save();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setIsGenerating(false);
      setFetchedMarks([]);
      setFetchedCoScholastic([]);
      setFetchedAttendance([]);
      setStudentRanks({});
    }
  };

  const getButtonClass = () => {
    const base = "relative flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-bold transition-all duration-300 shadow-sm overflow-hidden w-full sm:w-auto";
    
    if (isComplete) {
      // 🟢 Fully ready
      return `${base} bg-green-600 hover:bg-green-700 text-white shadow-green-500/50 shadow-lg`;
    }
    
    if (completionPercent > 0) {
      // 🟡 Partially complete
      return `${base} bg-indigo-50 border border-indigo-200 text-indigo-700 opacity-80 hover:opacity-100`;
    }
    
    // 🔴 Faded / Empty
    return `${base} bg-gray-100 border border-gray-200 text-gray-500 opacity-60 cursor-not-allowed hover:opacity-80`;
  };

  return (
    <>
      <button 
        onClick={handleDownloadClick}
        disabled={isGenerating}
        className={getButtonClass()}
      >
        {isGenerating ? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            Generating PDF...
          </span>
        ) : isComplete ? (
          <>
            <div className="absolute inset-0 bg-white/20 animate-pulse rounded-xl"></div>
            <Download size={18} />
            Download Report Cards
          </>
        ) : (
          <>
            <AlertTriangle size={18} className={completionPercent === 0 ? "text-gray-400" : "text-amber-500"} />
            <span>Generate Reports</span>
            {completionPercent > 0 && (
              <span className="ml-2 text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                {completionPercent}%
              </span>
            )}
          </>
        )}
      </button>

      <PendingMarksModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        pendingSubjects={pendingSubjects}
        classId={classId}
      />

      {/* ── Hidden DOM for PDF Rendering ── */}
      <div style={{ position: 'absolute', top: '-99999px', left: '-99999px' }}>
        <div ref={reportTemplateRef}>
          {isGenerating && students.map((student, index) => (
              <ClassReportTemplate
              key={student.id}
              student={student}
              marks={fetchedMarks}
              schemas={schemas}
              academicYear={academicYear}
              targetClass={classId}
              section={section}
              schoolName={schoolName}
              coScholasticGrades={fetchedCoScholastic}
              pageBreakBefore={index > 0}
              rank={studentRanks[student.id]}
              attendance={fetchedAttendance}
            />
          ))}
        </div>
      </div>
    </>
  );
}
